
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

/**
 * Uploads an avatar image to storage
 */
export async function uploadAvatar(file: File, userId: string): Promise<string | null> {
  try {
    if (!file) {
      console.error('No file provided for upload');
      toast.error('No file provided for upload');
      return null;
    }
    
    console.log('Starting avatar upload process for file:', file.name);
    
    // IMPORTANT: The bucket must be created by an admin in the Supabase dashboard
    // Client-side bucket creation is not allowed due to RLS policies
    
    // Optimize the image first
    const optimizedFile = await optimizeImage(file);
    console.log('Image optimized, proceeding with upload');
    
    // Generate unique filename with timestamp to prevent caching issues
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}-${Date.now()}.${fileExt}`;
    const filePath = `avatars/${fileName}`;
    console.log('Generated filename for upload:', filePath);
    
    // Attempt to upload to the storage bucket
    console.log('Uploading avatar to path:', filePath);
    const { error: uploadError, data } = await supabase.storage
      .from('public-media')
      .upload(filePath, optimizedFile, { 
        upsert: true,
        cacheControl: '3600'
      });

    if (uploadError) {
      console.error('Upload error:', uploadError);
      
      if (uploadError.message.includes('The resource was not found') || 
          uploadError.message.includes('storage/object-not-found')) {
        toast.error("Upload failed - bucket 'public-media' does not exist. Please contact administrator.");
        return null;
      } else if (uploadError.message.includes('policy')) {
        toast.error("Permission denied. Make sure you're signed in.");
        return null;
      } else {
        toast.error(`Failed to upload avatar: ${uploadError.message}`);
        return null;
      }
    }

    // Update profile with new avatar
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ avatar_url: filePath })
      .eq('id', userId);

    if (updateError) {
      console.error('Profile update error:', updateError);
      toast.error("Failed to update profile with new avatar");
      return null;
    }

    console.log('Avatar uploaded successfully:', filePath);
    toast.success("Avatar uploaded successfully");
    return filePath;
  } catch (error) {
    console.error('Avatar upload error:', error);
    toast.error("Failed to upload avatar");
    return null;
  }
}

/**
 * Gets a public URL for an avatar
 */
export function getAvatarUrl(fileName: string | null): string {
  if (!fileName) {
    return '';
  }
  
  // Check if the URL is already a full URL (like Slack avatar URLs)
  if (fileName.startsWith('http://') || fileName.startsWith('https://')) {
    return fileName;
  }
  
  // If not a full URL, treat it as a Supabase storage file
  try {
    const { data } = supabase.storage
      .from('public-media')
      .getPublicUrl(fileName);
    
    if (!data || !data.publicUrl) {
      console.error('Failed to get public URL for', fileName);
      return '';
    }
    
    // Append cache busting parameter to prevent browser caching
    const cacheBuster = `?t=${Date.now()}`;
    return data.publicUrl + cacheBuster;
  } catch (error) {
    console.error('Error getting avatar URL:', error);
    return '';
  }
}

/**
 * Optimizes an image for upload by converting it to a smaller size
 * and converting to JPEG format
 */
export const optimizeImage = async (file: File, maxSize = 800, quality = 0.8): Promise<File> => {
  return new Promise((resolve, reject) => {
    // Create a FileReader to read the file as a data URL
    const reader = new FileReader();
    
    reader.onload = (event) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      img.onload = () => {
        // Calculate dimensions while maintaining aspect ratio
        let width = img.width;
        let height = img.height;
        
        if (width > height && width > maxSize) {
          height = (height * maxSize) / width;
          width = maxSize;
        } else if (height > maxSize) {
          width = (width * maxSize) / height;
          height = maxSize;
        }
        
        // Set canvas dimensions and draw image
        canvas.width = width;
        canvas.height = height;
        ctx?.drawImage(img, 0, 0, width, height);
        
        // Convert to blob directly using canvas.toBlob
        // This avoids using data URLs with fetch which violates CSP
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to create image blob'));
              return;
            }
            
            const optimizedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            
            resolve(optimizedFile);
          },
          'image/jpeg',
          quality
        );
      };
      
      // Use data URL directly, which is allowed by CSP
      img.src = event.target?.result as string;
      
      img.onerror = () => {
        console.error('Image failed to load');
        reject(new Error('Failed to load image'));
      };
    };
    
    reader.onerror = () => {
      console.error('FileReader error:', reader.error);
      reject(new Error('Failed to read file'));
    };
    
    // Read file as data URL (allowed by CSP)
    reader.readAsDataURL(file);
  });
};
